// src/components/Control/Operator/PanTilt.tsx
// Поворотная камера 1 (нода pan_tilt на Pi: два MG90S на PCA9685).
//   robot/<id>/pan_tilt/cmd   <- {"pan","tilt"} | {"dpan","dtilt"} | {"cmd":"center"|"release"} |
//                                {"cmd":"config", ...}
//   robot/<id>/pan_tilt/state -> телеметрия (useTelemetry)
// PanTiltPad — кнопки поверх кадра камеры 1 (держите — поворачивает дальше),
// PanTiltSettings — карточка в «Настройки → Камеры».
import React, { useEffect, useRef, useState } from 'react';
import { useRobot, useTelemetryState } from '../robot/RobotContext';
import { PanTiltConfig, STALE, fresh } from '../robot/telemetry';
import { putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';

const STEP = 10;        // градусов за нажатие
const HOLD_STEP = 4;    // и каждые HOLD_MS, пока держат
const HOLD_MS = 120;

function usePanTilt() {
  useTick(1000);
  const { session, robotId } = useRobot();
  const { st, at } = useTelemetryState(t => ({ st: t.panTilt, at: t.panTiltAt }));
  const send = (msg: object) => putJson(session, `robot/${robotId}/pan_tilt/cmd`, msg);
  return { st: fresh(at, STALE.node) ? st : null, send };
}

// Кнопка, которая повторяет действие, пока её держат
const HoldButton: React.FC<{ label: string; title: string; onStep: (first: boolean) => void; className?: string }> = ({ label, title, onStep, className }) => {
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);
  return (
    <button type="button" className={className} aria-label={title} title={title}
            onPointerDown={e => {
              e.stopPropagation();
              onStep(true);
              stop();
              timer.current = setInterval(() => onStep(false), HOLD_MS);
            }}
            onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStep(true); } }}>
      {label}
    </button>
  );
};

export const PanTiltPad: React.FC = () => {
  const { st, send } = usePanTilt();
  if (!st || !st.ok || st.asleep) return null;
  const move = (dpan: number, dtilt: number) => (first: boolean) =>
    send({ dpan: dpan * (first ? STEP : HOLD_STEP), dtilt: dtilt * (first ? STEP : HOLD_STEP) });
  return (
    <div className="op-pantilt" onClick={e => e.stopPropagation()} role="group" aria-label="Поворот камеры">
      <HoldButton className="up" label="▲" title="Камеру вверх" onStep={move(0, 1)} />
      <HoldButton className="left" label="◀" title="Камеру влево" onStep={move(1, 0)} />
      <button type="button" className="center" aria-label="Камеру прямо" title="Камеру прямо"
              onClick={e => { e.stopPropagation(); send({ cmd: 'center' }); }}>⌂</button>
      <HoldButton className="right" label="▶" title="Камеру вправо" onStep={move(-1, 0)} />
      <HoldButton className="down" label="▼" title="Камеру вниз" onStep={move(0, -1)} />
      <span className="angles" aria-live="off">↔ {Math.round(-st.pan)}° ↕ {Math.round(st.tilt)}°</span>
    </div>
  );
};

const Num: React.FC<{ label: string; value: number; min: number; max: number; step?: number; onCommit: (v: number) => void }> = ({ label, value, min, max, step = 1, onCommit }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const v = Number(draft.replace(',', '.'));
    if (Number.isFinite(v)) onCommit(Math.min(max, Math.max(min, v)));
    setDraft(null);
  };
  return (
    <label className="op-num">
      <span>{label}</span>
      <input type="number" value={draft ?? value} min={min} max={max} step={step}
             onChange={e => setDraft(e.target.value)} onBlur={commit}
             onKeyDown={e => { if (e.key === 'Enter') commit(); }} />
    </label>
  );
};

export const PanTiltSettings: React.FC = () => {
  const { st, send } = usePanTilt();
  const [drag, setDrag] = useState<{ pan?: number; tilt?: number }>({});
  const cfg = (update: Partial<PanTiltConfig>) => send({ cmd: 'config', ...update });

  return (
    <div className="feature-card">
      <h3>
        <span>Поворотная камера</span>
        {!st ? <span className="badge off">нет данных</span>
          : st.ok ? <span className="badge ok">{st.asleep ? 'спит' : st.released ? 'отпущена' : 'на связи'}</span>
            : <span className="badge alert">нет платы</span>}
      </h3>
      {!st && <p className="hint">Нода pan_tilt на роботе не отвечает (не запущена или старая версия ПО на Pi).</p>}
      {st && !st.ok && <p className="error-text">PCA9685: {st.error}</p>}
      {st && (
        <>
          <div className="row">
            <label htmlFor="pt-pan">Поворот</label>
            <input id="pt-pan" type="range" min={-st.config.pan_max} max={-st.config.pan_min} step={1}
                   value={-(drag.pan ?? st.target.pan)} disabled={!st.ok}
                   onChange={e => setDrag(d => ({ ...d, pan: -Number(e.target.value) }))}
                   onPointerUp={() => { if (drag.pan !== undefined) send({ pan: drag.pan }); setDrag(d => ({ ...d, pan: undefined })); }} />
            <span className="hint">{Math.round(-st.pan)}° {st.pan > 0.5 ? 'влево' : st.pan < -0.5 ? 'вправо' : ''}</span>
          </div>
          <div className="row">
            <label htmlFor="pt-tilt">Наклон</label>
            <input id="pt-tilt" type="range" min={st.config.tilt_min} max={st.config.tilt_max} step={1}
                   value={drag.tilt ?? st.target.tilt} disabled={!st.ok}
                   onChange={e => setDrag(d => ({ ...d, tilt: Number(e.target.value) }))}
                   onPointerUp={() => { if (drag.tilt !== undefined) send({ tilt: drag.tilt }); setDrag(d => ({ ...d, tilt: undefined })); }} />
            <span className="hint">{Math.round(st.tilt)}° {st.tilt > 0.5 ? 'вверх' : st.tilt < -0.5 ? 'вниз' : ''}</span>
          </div>
          <div className="row">
            <button type="button" className="op-btn" disabled={!st.ok} onClick={() => send({ cmd: 'center' })}>⌂ Прямо</button>
            <button type="button" className="op-btn" disabled={!st.ok || st.released} onClick={() => send({ cmd: 'release' })}
                    title="Снять питание с серво: не жужжат и не тратят ток; камера может опуститься под своим весом">
              Отпустить серво
            </button>
          </div>
          <details>
            <summary>Калибровка и пределы</summary>
            <p className="hint">Подстройка — если «прямо» смотрит чуть в сторону; инверсия — если кнопка «влево» крутит вправо.</p>
            <div className="op-num-grid">
              <Num label="Подстройка поворота, °" value={st.config.pan_trim} min={-30} max={30} onCommit={v => cfg({ pan_trim: v })} />
              <Num label="Подстройка наклона, °" value={st.config.tilt_trim} min={-30} max={30} onCommit={v => cfg({ tilt_trim: v })} />
              <Num label="Поворот вправо до, °" value={-st.config.pan_min} min={0} max={90} onCommit={v => cfg({ pan_min: -v })} />
              <Num label="Поворот влево до, °" value={st.config.pan_max} min={0} max={90} onCommit={v => cfg({ pan_max: v })} />
              <Num label="Наклон вниз до, °" value={-st.config.tilt_min} min={0} max={90} onCommit={v => cfg({ tilt_min: -v })} />
              <Num label="Наклон вверх до, °" value={st.config.tilt_max} min={0} max={90} onCommit={v => cfg({ tilt_max: v })} />
              <Num label="Скорость, °/с" value={st.config.speed_dps} min={10} max={400} step={10} onCommit={v => cfg({ speed_dps: v })} />
            </div>
            <label className="op-check">
              <input type="checkbox" checked={st.config.pan_invert} onChange={e => cfg({ pan_invert: e.target.checked })} />
              Инвертировать поворот
            </label>
            <label className="op-check">
              <input type="checkbox" checked={st.config.tilt_invert} onChange={e => cfg({ tilt_invert: e.target.checked })} />
              Инвертировать наклон
            </label>
          </details>
          <p className="hint">Кнопки поворота — поверх кадра камеры 1. Во сне серво отпускаются, после пробуждения камера
            возвращается. Зрение и следование за человеком учитывают поворот камеры.</p>
        </>
      )}
    </div>
  );
};
